// Original, bounded browser adapters. No external source JavaScript is loaded.
// CORS failures remain explicit; playback may pass an approved URL to native
// Audio, while downloads still require readable, verified response bytes.
import { readAudioProbe, type AudioProbe } from './audio-probe.ts';
const SOURCES = {
  yuxi: {
    wy: { standard: 'standard', exhigh: 'exhigh', lossless: 'lossless', hires: 'hires', jyeffect: 'jyeffect', jymaster: 'jymaster' },
    tx: { standard: 'standard', exhigh: 'exhigh', lossless: 'lossless', flac24bit: '2599', atmos: '2599', atmos_plus: '2999', jymaster: 'jymaster' },
  },
  yuningxi: { tx: { standard: 'song_play_url_standard', exhigh: 'song_play_url_hq', lossless: 'song_play_url_sq' } },
} as const;
const RANK: Record<string, number> = { standard: 0, exhigh: 1, lossless: 2, hires: 3, flac24bit: 3, jyeffect: 4, atmos: 4, atmos_plus: 5, jymaster: 6 };
const MEDIA_SUFFIXES = ['music.126.net', 'music.163.com', 'stream.qqmusic.qq.com', 'qqmusic.tc.qq.com', 'kuwo.cn'];
// These Kuwo CDN hosts frequently omit ACAO while still being valid native
// media candidates. In the real browser path, skip the doomed byte fetch and
// let the server-side playback probe measure the selected URL after Audio
// starts. Injected fetchers used by tests and diagnostics keep the strict probe
// path so malformed content is still rejected there.
const NATIVE_ONLY_MEDIA_HOSTS = new Set(['car-er.kuwo.cn', 'car-lv.kuwo.cn']);
const error = (code: string) => Object.assign(new Error(code), { code });
const httpRequestError = (status: number, url: string, preview: boolean) => {
  const code = [401, 403].includes(status) ? 'AUTH_REQUIRED' : status === 429 ? 'RATE_LIMITED' : 'SOURCE_UNAVAILABLE';
  const service = preview ? '音频服务器' : '音源解析服务';
  const reason = code === 'AUTH_REQUIRED' ? '拒绝访问' : code === 'RATE_LIMITED' ? '请求过于频繁' : status >= 500 ? '暂时不可用' : '请求失败';
  return Object.assign(error(code), {
    message: `${service}${reason}（HTTP ${status}），${code === 'AUTH_REQUIRED' ? '请检查音源访问权限' : '请稍后重试'}。`,
    // Keep the failed boundary, never the signed path/query or response body.
    diagnostics: { stage: preview ? 'media-probe' : 'resolve', hostname: new URL(url).hostname, httpStatus: status },
  });
};
const blockedMediaUrl = (url?: URL) => Object.assign(error('SOURCE_URL_BLOCKED'), {
  diagnostics: { stage: 'media-url', protocol: url?.protocol.slice(0, 20) || '', hostname: url?.hostname.slice(0, 253) || '' },
});
type Json = Record<string, any>;
type BrowserSourceOptions = { fetcher?: typeof fetch; timeoutMs?: number; now?: () => number };
export type BrowserOnlineResolution = AudioProbe & {
  sourceId: string; trackId: string; mediaUrl: string; url: string;
  requestedQuality: string; effectiveQuality: string; quality: string; fallback: boolean;
  attemptedQualities: string[]; nativeQuality: string; format: string; mime: string;
  upstreamMime: string; qualityVerified: false; playbackVerified: false; byteVerified: boolean;
  nativeCandidate: boolean; nativeProbeError?: string; expiresAt: string;
  transport: 'browser';
};

const trial = (data: Json) => [data, data?.data].some(value => value && (value.trial === true || value.trial === 1 || value.trial === '1' || value.freeTrialInfo || value.free_trial_info));
function canonicalQuality(platform: string, value: string) {
  const aliases: Record<string, string> = { '128k': 'standard', '320k': 'exhigh', flac: 'lossless', master: 'jymaster', ...(platform === 'wy' ? { flac24bit: 'hires', atmos: 'jyeffect' } : {}) };
  return aliases[value] || value;
}
function parseTrack(trackId: string) {
  const match = /^online:(yuxi|yuningxi):(wy|tx):([A-Za-z0-9]{1,64})$/.exec(trackId);
  if (!match) throw error('SOURCE_TRACK_INVALID');
  const [, sourceId, platform, externalId] = match;
  const mappings = SOURCES[sourceId as keyof typeof SOURCES] as Record<string, Record<string, string>>;
  if (!mappings[platform] || (platform === 'wy' && !/^\d+$/.test(externalId))) throw error('SOURCE_TRACK_INVALID');
  return { sourceId, platform, externalId, mapping: mappings[platform] };
}
export function assertBrowserMediaUrl(value: unknown) {
  let url: URL;
  try { url = new URL(String(value)); } catch { throw blockedMediaUrl(); }
  if (url.protocol === 'http:') url.protocol = 'https:';
  if (url.protocol !== 'https:' || url.username || url.password || url.hash || (url.port && url.port !== '443') ||
    (!['car-er.kuwo.cn', 'car-lv.kuwo.cn'].includes(url.hostname) && !MEDIA_SUFFIXES.some(host => url.hostname === host || url.hostname.endsWith(`.${host}`)))) throw blockedMediaUrl(url);
  return url;
}
// Shared by the playback bridge and download engine. Returning the normalized
// string ensures that neither consumer accidentally keeps an HTTP URL.
export const approvedDirectMediaUrl = (value: unknown) => assertBrowserMediaUrl(value).toString();
function audioFormat(bytes: Uint8Array) {
  const ascii = (start: number, end: number) => String.fromCharCode(...bytes.subarray(start, end));
  if (bytes.length < 4) return null;
  if (ascii(0, 4) === 'fLaC') return { mime: 'audio/flac', format: 'flac' };
  if (bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WAVE') return { mime: 'audio/wav', format: 'wav' };
  if (bytes.length >= 12 && ascii(4, 8) === 'ftyp') return { mime: 'audio/mp4', format: 'mp4' };
  if (ascii(0, 4) === 'OggS') return { mime: 'audio/ogg', format: 'ogg' };
  if (ascii(0, 3) === 'ID3') return { mime: 'audio/mpeg', format: 'mp3' };
  if (bytes[0] === 0xff && (bytes[1] & 0xf6) === 0xf0) return { mime: 'audio/aac', format: 'aac' };
  if (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0 && (bytes[1] & 6) !== 0) return { mime: 'audio/mpeg', format: 'mp3' };
  return null;
}
async function readBytes(response: Response, limit: number, preview = false) {
  if (!response.body) throw error('SOURCE_RESPONSE_INVALID');
  const reader = response.body.getReader(); const chunks: Uint8Array[] = []; let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const remaining = limit - size;
      if (!preview && value.length > remaining) throw error('SOURCE_RESPONSE_TOO_LARGE');
      const kept = value.subarray(0, remaining); chunks.push(kept); size += kept.length;
      if (preview && size >= limit) break;
    }
  } finally { await reader.cancel().catch(() => {}); }
  const bytes = new Uint8Array(size); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  return bytes;
}
function resolverUrl(data: Json) {
  if (trial(data)) throw error('SOURCE_TRIAL_UNSUPPORTED');
  if (typeof data?.code !== 'number') throw error('SOURCE_RESPONSE_INVALID');
  const resolverFailure = (code: string, message: string) => Object.assign(error(code), {
    message: `音源解析服务${message}（业务代码 ${data.code}），${code === 'AUTH_REQUIRED' ? '请检查音源访问权限' : '请稍后重试'}。`,
    diagnostics: { stage: 'resolve', hostname: 'musicserver.haitangw.cc', upstreamCode: data.code },
  });
  if ([401, 403].includes(data.code)) throw resolverFailure('AUTH_REQUIRED', '拒绝访问');
  if (data.code === 429) throw resolverFailure('RATE_LIMITED', '请求过于频繁');
  // 503 is a resolver service failure. Keep the established same-recording
  // quality fallback only for the explicit per-quality timeout marker.
  if (data.code === 503 && !/upstream[_ -]?timeout/i.test(String(data.message || data.msg || ''))) throw resolverFailure('SOURCE_UNAVAILABLE', '暂时不可用');
  if (data.code !== 0) {
    const message = String(data.message || data.msg || '');
    // Some resolver nodes return an HTTP-success envelope with an explicit
    // per-quality UPSTREAM_TIMEOUT. Keep the retry bounded to this recording's
    // lower quality candidates; generic service errors still stop immediately.
    const missing = data.code === 404 || /upstream[_ -]?timeout/i.test(message) || /^(?:unavailable|not found)$/i.test(message) || /(?:quality|level|音质|品质|档位).*(?:unavailable|unsupported|not supported|not found|不可用|不支持|不存在)/i.test(message);
    throw error((data.code < 500 && missing) || /upstream[_ -]?timeout/i.test(message) ? 'SOURCE_QUALITY_UNAVAILABLE' : 'SOURCE_UNAVAILABLE');
  }
  if (typeof data.data?.url !== 'string' || !data.data.url) throw error('SOURCE_QUALITY_UNAVAILABLE');
  return data.data.url;
}
function rejectTangBusinessStatus(data: Json) {
  const code = typeof data?.code === 'number' ? data.code : undefined;
  if (code === undefined || code === 0) return;
  const mapped = [401, 403].includes(code) ? 'AUTH_REQUIRED' : code === 429 ? 'RATE_LIMITED' : code === 503 ? 'SOURCE_UNAVAILABLE' : undefined;
  if (!mapped) return;
  const reason = mapped === 'AUTH_REQUIRED' ? '拒绝访问' : mapped === 'RATE_LIMITED' ? '请求过于频繁' : '暂时不可用';
  throw Object.assign(error(mapped), {
    message: `音源解析服务${reason}（业务代码 ${code}），${mapped === 'AUTH_REQUIRED' ? '请检查音源访问权限' : '请稍后重试'}。`,
    diagnostics: { stage: 'resolve', hostname: 'tang.api.s01s.cn', upstreamCode: code },
  });
}

export function createBrowserOnlineSources({ fetcher = globalThis.fetch, timeoutMs = 15000, now = Date.now }: BrowserSourceOptions = {}) {
  const request = async (url: string, options: RequestInit, signal: AbortSignal | undefined, preview = false) => {
    const combined = signal ? AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]) : AbortSignal.timeout(timeoutMs);
    try {
      const response = await fetcher(url, { ...options, mode: 'cors', credentials: 'omit', redirect: 'error', signal: combined });
      if (response.type === 'opaque' || response.type === 'opaqueredirect') throw error('SOURCE_CORS_UNAVAILABLE');
      if (!response.ok) throw httpRequestError(response.status, url, preview);
      return { response, bytes: await readBytes(response, preview ? 1024 : 1024 * 1024, preview) };
    } catch (cause) {
      if (signal?.aborted) throw error('REQUEST_CANCELLED');
      if (combined.aborted) throw error('SOURCE_TIMEOUT');
      if ((cause as { code?: string })?.code) throw cause;
      // Browsers deliberately do not distinguish CORS rejection from many
      // network failures. Keep that boundary explicit instead of claiming CORS.
      throw error('SOURCE_BROWSER_NETWORK');
    }
  };
  const json = async (url: string, options: RequestInit, signal?: AbortSignal): Promise<Json> => {
    const { bytes } = await request(url, options, signal);
    try { return JSON.parse(new TextDecoder().decode(bytes)); } catch { throw error('SOURCE_RESPONSE_INVALID'); }
  };
  return {
    async resolve(trackId: string, quality = 'standard', signal?: AbortSignal, { purpose = 'download' }: { purpose?: 'playback' | 'download' } = {}): Promise<BrowserOnlineResolution> {
      const { sourceId, platform, externalId, mapping } = parseTrack(trackId);
      const requestedQuality = canonicalQuality(platform, quality);
      if (!Object.hasOwn(RANK, requestedQuality)) throw error('SOURCE_QUALITY_UNSUPPORTED');
      const seen = new Set<string>();
      const qualities = Object.keys(mapping).filter(value => RANK[value] <= RANK[requestedQuality]).sort((a, b) => RANK[b] - RANK[a]).filter(value => {
        if (seen.has(mapping[value])) return false; seen.add(mapping[value]); return true;
      });
      const attemptedQualities: string[] = []; let tangResult: Json | undefined; let lastError: unknown; let mediaError: unknown;
      for (const effectiveQuality of qualities) {
        if (signal?.aborted) throw error('REQUEST_CANCELLED');
        const nativeQuality = mapping[effectiveQuality]; attemptedQualities.push(effectiveQuality);
        let checkingMedia = false; let upstream: unknown; let approvedUrl: string | undefined;
        try {
          if (sourceId === 'yuxi') {
            upstream = resolverUrl(await json('https://musicserver.haitangw.cc/v1/music/resolve-url', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ source: platform, rid: externalId, level: nativeQuality }) }, signal));
          } else {
            if (!tangResult) {
              tangResult = await json(`https://tang.api.s01s.cn/music_open_api.php?mid=${encodeURIComponent(externalId)}`, {}, signal);
              if (trial(tangResult)) throw error('SOURCE_TRIAL_UNSUPPORTED');
              rejectTangBusinessStatus(tangResult);
              if (String(tangResult.song_mid) !== externalId) throw error('SOURCE_TRACK_MISMATCH');
            }
            upstream = tangResult[nativeQuality];
            if (typeof upstream !== 'string' || !upstream) throw error('SOURCE_QUALITY_UNAVAILABLE');
          }
          checkingMedia = true;
          const mediaUrl = approvedUrl = approvedDirectMediaUrl(upstream);
          if (purpose === 'playback' && fetcher === globalThis.fetch && NATIVE_ONLY_MEDIA_HOSTS.has(new URL(mediaUrl).hostname)) {
            return { sourceId, trackId, mediaUrl, url: mediaUrl, requestedQuality, effectiveQuality, quality: effectiveQuality, fallback: effectiveQuality !== requestedQuality, attemptedQualities, nativeQuality, format: 'unknown', mime: '', upstreamMime: '', ...readAudioProbe(new Uint8Array()), qualityVerified: false, playbackVerified: false, byteVerified: false, nativeCandidate: true, nativeProbeError: 'SOURCE_CORS_UNAVAILABLE', expiresAt: new Date(now() + 5 * 60_000).toISOString(), transport: 'browser' };
          }
          const { response, bytes } = await request(mediaUrl, { headers: { Range: 'bytes=0-1023' } }, signal, true);
          const upstreamMime = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
          const knownQqMislabel = new URL(mediaUrl).hostname === 'isure.stream.qqmusic.qq.com' && upstreamMime === 'application/x-www-form-urlencoded';
          const actual = audioFormat(bytes);
          if (![200, 206].includes(response.status) || !(/^audio\//.test(upstreamMime) || upstreamMime === 'application/octet-stream' || knownQqMislabel) || !actual) throw error('SOURCE_STREAM_INVALID');
          const parameters = readAudioProbe(bytes);
          if (RANK[effectiveQuality] >= RANK.lossless && (['mp3', 'aac'].includes(actual.format) || parameters.codec === 'aac')) throw error('SOURCE_QUALITY_MISMATCH');
          return { sourceId, trackId, mediaUrl, url: mediaUrl, requestedQuality, effectiveQuality, quality: effectiveQuality, fallback: effectiveQuality !== requestedQuality, attemptedQualities, nativeQuality, ...actual, ...parameters, upstreamMime, qualityVerified: false, playbackVerified: false, byteVerified: true, nativeCandidate: false, expiresAt: new Date(now() + 5 * 60_000).toISOString(), transport: 'browser' };
        } catch (cause) {
          if (signal?.aborted) throw error('REQUEST_CANCELLED');
          const { code = '' } = cause as { code?: string };
          // Native Audio can decode a cross-origin resource without exposing
          // its bytes to JavaScript. Do not lower the requested quality or
          // claim verification simply because a readable CORS probe failed.
          if (purpose === 'playback' && approvedUrl && ['SOURCE_BROWSER_NETWORK', 'SOURCE_CORS_UNAVAILABLE', 'SOURCE_TIMEOUT'].includes(code)) {
            return { sourceId, trackId, mediaUrl: approvedUrl, url: approvedUrl, requestedQuality, effectiveQuality, quality: effectiveQuality, fallback: effectiveQuality !== requestedQuality, attemptedQualities, nativeQuality, format: 'unknown', mime: '', upstreamMime: '', ...readAudioProbe(new Uint8Array()), qualityVerified: false, playbackVerified: false, byteVerified: false, nativeCandidate: true, nativeProbeError: code, expiresAt: new Date(now() + 5 * 60_000).toISOString(), transport: 'browser' };
          }
          // A resolver can return a broken high-quality CDN while this exact
          // recording's lower quality is usable. Do not fetch blocked hosts,
          // change recordings, or turn an API/service failure into a retry.
          if (checkingMedia && ['SOURCE_BROWSER_NETWORK', 'SOURCE_CORS_UNAVAILABLE', 'SOURCE_TIMEOUT', 'SOURCE_STREAM_INVALID', 'SOURCE_UNAVAILABLE', 'AUTH_REQUIRED'].includes(code)) {
            mediaError = cause;
            continue;
          }
          if (!['SOURCE_QUALITY_UNAVAILABLE', 'SOURCE_QUALITY_MISMATCH'].includes(code)) throw cause;
          if (code === 'SOURCE_QUALITY_MISMATCH') mediaError = cause;
          lastError = cause;
        }
      }
      throw mediaError || lastError || error('SOURCE_QUALITY_UNAVAILABLE');
    },
  };
}

const browserSources = createBrowserOnlineSources();
export const resolveBrowserPlayback = (trackId: string, quality = 'standard', signal?: AbortSignal) => browserSources.resolve(trackId, quality, signal, { purpose: 'playback' });
export const resolveBrowserDownload = (trackId: string, quality = 'standard', signal?: AbortSignal) => browserSources.resolve(trackId, quality, signal, { purpose: 'download' });
export const resolveBrowserOnlinePlayback = resolveBrowserPlayback;
